/**
 * Mensagens da reserva de horário para "Copiar" / "Abrir no WhatsApp".
 * Mesmos modelos do envio pelo número próprio
 * (public/api/appointments/reservations/ReservationMessage.php), com o link no fim
 * em vez do botão.
 */

import MessagesTemplate from '../../../../consts/MessagesTemplate';

export const RESERVATION_TEMPLATE_KEY = 'AppointmentReservationMessage';
export const REMINDER_TEMPLATE_KEY = 'AppointmentReservationReminder';
export const RELEASED_TEMPLATE_KEY = 'AppointmentReservationReleased';

const WEEKDAYS = ['domingo', 'segunda-feira', 'terça-feira', 'quarta-feira', 'quinta-feira', 'sexta-feira', 'sábado'];
const MONTHS = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

const pad = (n) => String(n).padStart(2, '0');
const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

/** "2026-10-14 09:30:00" → Date local (sem conversão de fuso). */
export function parseReservationDate(value) {
  const [datePart, timePart = '00:00:00'] = String(value || '').split(' ');
  const [y, m, d] = datePart.split('-').map(Number);
  const [h, min] = timePart.split(':').map(Number);
  return new Date(y, (m || 1) - 1, d || 1, h || 0, min || 0, 0, 0);
}

/** "quarta-feira, 14/10, às 09:30" */
export function describeReservationSlot(value) {
  const when = parseReservationDate(value);
  return `${WEEKDAYS[when.getDay()]}, ${pad(when.getDate())}/${pad(when.getMonth() + 1)}, às ${pad(when.getHours())}:${pad(when.getMinutes())}`;
}

/**
 * Prazo do link de cadastro: "hoje, às 15:30", "amanhã, às 09:00" ou
 * "sexta-feira, 16/10, às 09:00". Igual a ReservationMessage::deadline().
 * Desde 2026-11-04 o texto padrão não cita mais o prazo e o editor de modelos
 * não oferece {deadline}; segue preenchida para modelos antigos que ainda a usam.
 */
export function describeDeadline(value) {
  if (!value) return '';
  const at = parseReservationDate(value);
  const time = `${pad(at.getHours())}:${pad(at.getMinutes())}`;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const day = new Date(at.getFullYear(), at.getMonth(), at.getDate());
  const days = Math.round((day - today) / 86400000);
  if (days === 0) return `hoje, às ${time}`;
  if (days === 1) return `amanhã, às ${time}`;
  return `${WEEKDAYS[at.getDay()]}, ${pad(at.getDate())}/${pad(at.getMonth() + 1)}, às ${time}`;
}

export function reservationLink(token) {
  return `${window.location.origin}/cadastro/${token}`;
}

/**
 * Preenche o modelo da reserva com as mesmas variáveis do backend
 * (ReservationMessage::build). Linha "Rótulo: {var}" some quando a variável
 * está vazia, como em substituteTemplateVariables().
 */
function renderReservationTemplate(template, reservation, clinicName) {
  const when = parseReservationDate(reservation.date);
  const values = {
    company_name: String(clinicName || '').trim(),
    professional_name: String(reservation.professional_name || '').trim(),
    procedure_name: String(reservation.procedure_name || '').trim(),
    when: capitalize(WEEKDAYS[when.getDay()]),
    day: pad(when.getDate()),
    month: capitalize(MONTHS[when.getMonth()]),
    time: `${pad(when.getHours())}:${pad(when.getMinutes())}`,
    deadline: describeDeadline(reservation.expires_at),
  };

  let text = MessagesTemplate.ConvertToClipboardText(template || '');
  Object.entries(values).forEach(([key, value]) => {
    if (value === '') {
      text = text.replace(new RegExp(`^[^\\n{}]{1,40}:[ \\t]*\\{${key}\\}[ \\t]*\\n?`, 'gm'), '');
    }
  });
  Object.entries(values).forEach(([key, value]) => {
    text = text.replaceAll(`{${key}}`, value);
  });
  return text.replace(/\n{3,}/g, '\n\n').trim();
}

/**
 * Modelo da empresa (ou o padrão) já preenchido, com o link de cadastro no fim.
 *
 * @param {Object} reservation - payload de reservations/*.php
 * @param {Object} companyConfig - CompanyConfig do DataProvider (modelos + nome)
 * @param {string} [templateKey] - RESERVATION_TEMPLATE_KEY ou REMINDER_TEMPLATE_KEY
 */
export function buildReservationMessage(reservation, companyConfig, templateKey = RESERVATION_TEMPLATE_KEY) {
  const template = MessagesTemplate.getTemplate(templateKey, companyConfig);
  const text = renderReservationTemplate(template, reservation, companyConfig?.Name);
  return `${text}\n\n${reservationLink(reservation.token)}`;
}

/**
 * Aviso de horário liberado: modelo AppointmentReservationReleased, como em
 * ReservationMessage::buildReleased(). O backend só conhece o horário da reserva
 * liberada, então aqui também só vai a data (sem profissional nem serviço).
 */
export function buildReleasedMessage(reservation, companyConfig) {
  const template = MessagesTemplate.getTemplate(RELEASED_TEMPLATE_KEY, companyConfig);
  return renderReservationTemplate(template, { date: reservation.date }, companyConfig?.Name);
}

/** wa.me já endereçado ao paciente, com a mensagem pronta. */
export function reservationWhatsAppUrl(reservation, message) {
  const number = `${reservation.country_code || '55'}${reservation.phone}`.replace(/\D/g, '');
  return `https://wa.me/${number}?text=${encodeURIComponent(message)}`;
}

/** (11) 98888-7777, com o DDI na frente quando não for Brasil. */
export function formatReservationPhone(countryCode, phone) {
  const d = String(phone || '').replace(/\D/g, '');
  let local = d;
  if (d.length === 11) local = `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  else if (d.length === 10) local = `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  const cc = String(countryCode || '55');
  return cc === '55' ? local : `+${cc} ${local}`;
}
